import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useBasket, BasketProvider } from '../context/BasketContext';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Wifi, WifiOff, RefreshCw, LogOut, Tag, KeyRound,
  PauseCircle, PlayCircle, Award, Phone, X
} from 'lucide-react';
import { ChangePinDialog } from '../components/ChangePinDialog';
import { CategorySidebar } from '../components/till/CategorySidebar';
import { ProductGrid } from '../components/till/ProductGrid';
import { Basket } from '../components/till/Basket';
import {
  PaymentModal, SaleConfirmation, DiscountModal, OfflineQueueDialog,
  CustomerSearchDialog, NewCustomerDialog, RedemptionDialog, DeferredOrdersDialog
} from '../components/till/TillModals';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TillContent = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { items, total, clearBasket, addItem, addAddon } = useBasket();
  const { isOnline, pendingCount, isSyncing, syncNow, queueSale, getPendingSales } = useOfflineStatus();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [displayGroup, setDisplayGroup] = useState(null);
  const [completedSale, setCompletedSale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOfflineQueue, setShowOfflineQueue] = useState(false);
  const [pendingSales, setPendingSales] = useState([]);
  const [settings, setSettings] = useState(null);
  const [vatRate, setVatRate] = useState(20);
  const [discount, setDiscount] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [linkedCustomer, setLinkedCustomer] = useState(null);
  const [customerPhone, setCustomerPhone] = useState('');
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [showRedemption, setShowRedemption] = useState(false);
  const [redemptionChoice, setRedemptionChoice] = useState(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustForm, setNewCustForm] = useState({ phone: '', first_name: '', surname: '', gdpr_consent: true });
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [deferredOrders, setDeferredOrders] = useState([]);
  const [showDeferredDialog, setShowDeferredDialog] = useState(false);
  const [favourites, setFavourites] = useState({});

  useEffect(() => { fetchData(); fetchSettings(); fetchLoyaltySettings(); fetchDeferredOrders(); fetchFavourites(); }, []);

  useEffect(() => {
    if (categories.length > 0 && settings) selectCategoryByTime();
  }, [categories, settings]);

  useEffect(() => { if (isOnline && pendingCount > 0) handleSync(); }, [isOnline]);

  const fetchData = async () => {
    try {
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/products`)
      ]);
      setCategories(catRes.data);
      setProducts(prodRes.data);
    } catch (error) {
      if (!isOnline) toast.info('Working offline - using cached data');
      else toast.error('Failed to load products');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/till`);
      setSettings(response.data);
      setVatRate(response.data.vat_rate || 20);
    } catch (error) { console.error('Error fetching settings:', error); }
  };

  const fetchLoyaltySettings = async () => {
    try { const res = await axios.get(`${API}/loyalty/settings`); setLoyaltySettings(res.data); }
    catch (err) { console.error('Error fetching loyalty settings:', err); }
  };

  const fetchDeferredOrders = async () => {
    try { const res = await axios.get(`${API}/deferred-orders`); setDeferredOrders(res.data); }
    catch (err) { console.error(err); }
  };

  const fetchFavourites = async () => {
    try { const res = await axios.get(`${API}/products/favourites?limit=8`); setFavourites(res.data); }
    catch (err) { console.error('Error fetching favourites:', err); }
  };

  const selectCategoryByTime = () => {
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    for (const cat of categories) {
      if (cat.time_start && cat.time_end && currentTime >= cat.time_start && currentTime <= cat.time_end) {
        if (cat.display_group) { setDisplayGroup(cat.display_group); setActiveCategory(null); }
        else { setActiveCategory(cat.id); setDisplayGroup(null); }
        return;
      }
    }
    if (categories.length > 0) { setActiveCategory(categories[0].id); setDisplayGroup(null); }
  };

  const handleCategorySelect = (catId) => {
    if (catId === null) { setDisplayGroup(null); setActiveCategory(categories[0]?.id || null); }
    else {
      const cat = categories.find(c => c.id === catId);
      if (cat?.display_group) { setDisplayGroup(cat.display_group); setActiveCategory(null); }
      else { setActiveCategory(catId); setDisplayGroup(null); }
    }
  };

  const handleSync = async () => {
    const result = await syncNow();
    if (result) {
      if (result.synced > 0) {
        toast.success(`Synced ${result.synced} offline sale(s)`);
        try { const prodRes = await axios.get(`${API}/products`); setProducts(prodRes.data); } catch (e) {}
      }
      if (result.failed > 0) toast.warning(`${result.failed} sale(s) failed to sync`);
    }
  };

  const handleAddToBasket = (product, isAddon = false) => {
    if (isAddon) addAddon({ ...product, unit_price_inc_vat: product.price_inc_vat });
    else addItem({ ...product, unit_price_inc_vat: product.price_inc_vat });
  };

  const handleCompleteSale = (subtotal, vatAmount, finalTotal, discountAmount) => {
    setPaymentData({ subtotal, vatAmount, total: finalTotal, discount: discountAmount });
    setShowPayment(true);
  };

  const handlePaymentConfirm = async (paymentInfo) => {
    setShowPayment(false);
    setLoading(true);
    const saleData = {
      items: items.map(i => ({
        product_id: i.product_id, product_name: i.product_name, quantity: i.quantity,
        unit_price: i.unit_price, unit_price_inc_vat: i.unit_price_inc_vat,
        vat_amount: i.unit_price * (vatRate / 100) * i.quantity
      })),
      subtotal: paymentData.subtotal, vat_total: paymentData.vatAmount,
      total: paymentData.total, discount, payment_method: paymentInfo.paymentMethod,
      account_signatory: paymentInfo.accountSignatory,
      cash_tendered: paymentInfo.cashTendered, cash_change: paymentInfo.cashChange,
      customer_id: linkedCustomer?.id || null, loyalty_redemption: redemptionChoice
    };

    if (isOnline) {
      try {
        const response = await axios.post(`${API}/sales`, saleData);
        setCompletedSale(response.data);
        clearBasket(); setDiscount(null); setLinkedCustomer(null); setRedemptionChoice(null);
        const prodRes = await axios.get(`${API}/products`); setProducts(prodRes.data);
        if (paymentInfo.printReceipt) handlePrintReceipt(response.data.id);
        toast.success('Sale completed successfully!');
      } catch (error) {
        if (!error.response) {
          const queuedSale = await queueSale(saleData);
          setCompletedSale({ id: queuedSale.localId, order_number: null, items: saleData.items, total: saleData.total, payment_method: saleData.payment_method, created_at: queuedSale.createdAt, offline: true });
          clearBasket(); setDiscount(null);
          toast.info('Sale queued - will sync when online');
        } else {
          toast.error(error.response?.data?.detail || 'Failed to complete sale');
        }
      }
    } else {
      try {
        const queuedSale = await queueSale(saleData);
        setProducts(prev => prev.map(p => { const item = items.find(i => i.product_id === p.id); return item ? { ...p, stock_qty: Math.max(0, p.stock_qty - item.quantity) } : p; }));
        setCompletedSale({ id: queuedSale.localId, order_number: null, items: saleData.items, total: saleData.total, payment_method: saleData.payment_method, created_at: queuedSale.createdAt, offline: true });
        clearBasket(); setDiscount(null);
        toast.info('Sale queued offline - will sync when online');
      } catch (error) { toast.error('Failed to queue sale'); }
    }
    setLoading(false);
  };

  const handlePrintReceipt = async (saleId) => {
    try {
      const response = await axios.get(`${API}/receipts/${saleId}`);
      const receipt = response.data;
      const printWindow = window.open('', '_blank', 'width=400,height=700');
      printWindow.document.write(`<html><head><title>Receipt - ${receipt.receipt_number}</title>
        <style>body{font-family:'Courier New',monospace;font-size:12px;padding:20px;max-width:300px;margin:0 auto}.center{text-align:center}.bold{font-weight:bold}.line{border-top:1px dashed #000;margin:10px 0}.item{display:flex;justify-content:space-between;margin:4px 0}.total{font-size:14px;font-weight:bold}@media print{body{padding:0}}</style></head>
        <body><div class="center bold" style="font-size:16px">${receipt.business_name.toUpperCase()}</div>
        ${receipt.vat_number ? `<div class="center">VAT No: ${receipt.vat_number}</div>` : ''}<div class="line"></div>
        <div>Receipt: ${receipt.receipt_number}</div><div>Order #: ${receipt.order_number}</div>
        <div>Date: ${new Date(receipt.date).toLocaleString()}</div><div>Operator: ${receipt.operator}</div><div class="line"></div>
        ${receipt.items.map(item => `<div class="item"><span>${item.quantity}x ${item.product_name}</span><span>£${(item.quantity * (item.unit_price_inc_vat || item.unit_price)).toFixed(2)}</span></div>`).join('')}
        <div class="line"></div><div class="item"><span>Subtotal</span><span>£${receipt.subtotal.toFixed(2)}</span></div>
        <div class="item"><span>VAT @ ${receipt.vat_rate}%</span><span>£${receipt.vat_total.toFixed(2)}</span></div>
        ${receipt.discount ? `<div class="item" style="color:green"><span>Discount</span><span>-£${(receipt.discount.type === 'percentage' ? receipt.subtotal * receipt.discount.value / 100 : receipt.discount.value).toFixed(2)}</span></div>` : ''}
        <div class="item total"><span>TOTAL</span><span>£${receipt.total.toFixed(2)}</span></div><div class="line"></div>
        <div class="center">Payment: ${receipt.payment_method.toUpperCase()}</div>
        ${receipt.account_signatory ? `<div class="line"></div><div>Signatory: ${receipt.account_signatory}</div><div style="margin-top:30px;border-bottom:1px solid #000"></div><div class="center" style="font-size:10px">Signature</div>` : ''}
        <div class="line"></div><div class="center bold">VAT RECEIPT</div><div class="center">${receipt.footer_message}</div></body></html>`);
      printWindow.document.close(); printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
    } catch (error) { toast.error('Failed to print receipt'); }
  };

  const searchCustomer = async () => {
    if (!customerPhone.trim()) return;
    try {
      const res = await axios.get(`${API}/customers/search?phone=${encodeURIComponent(customerPhone.trim())}`);
      if (res.data.found) {
        setLinkedCustomer(res.data.customer); setShowCustomerSearch(false); setCustomerPhone('');
        toast.success(`${res.data.customer.first_name} linked (${res.data.customer.points_balance} pts)`);
      } else {
        toast.error('Customer not found');
        setNewCustForm(f => ({ ...f, phone: customerPhone.trim() }));
        setShowNewCustomer(true);
      }
    } catch (err) { toast.error('Search failed'); }
  };

  const createAndLinkCustomer = async () => {
    if (!newCustForm.phone || !newCustForm.first_name || !newCustForm.surname) { toast.error('All fields required'); return; }
    try {
      const res = await axios.post(`${API}/customers`, newCustForm);
      setLinkedCustomer(res.data); setShowNewCustomer(false); setShowCustomerSearch(false); setCustomerPhone('');
      setNewCustForm({ phone: '', first_name: '', surname: '', gdpr_consent: true });
      toast.success(`${res.data.first_name} registered & linked`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleParkOrder = async () => {
    if (items.length === 0) { toast.error('No items to park'); return; }
    try {
      await axios.post(`${API}/deferred-orders`, {
        items: items.map(i => ({ product_id: i.product_id, product_name: i.product_name, quantity: i.quantity, unit_price: i.unit_price, unit_price_inc_vat: i.unit_price_inc_vat })),
        customer_id: linkedCustomer?.id || null, note: null
      });
      clearBasket(); toast.success('Order parked — customer can pay later'); fetchDeferredOrders();
    } catch (err) { toast.error('Failed to park order'); }
  };

  const handleRecallOrder = async (order) => {
    if (items.length > 0) { toast.error('Clear the current basket first'); return; }
    for (const item of order.items) {
      addItem({ id: item.product_id, name: item.product_name, price: item.unit_price, price_inc_vat: item.unit_price_inc_vat || item.unit_price, unit_price_inc_vat: item.unit_price_inc_vat || item.unit_price });
    }
    try { await axios.post(`${API}/deferred-orders/${order.id}/complete`); } catch (err) {}
    setShowDeferredDialog(false);
    toast.success('Order recalled — ready to complete payment');
    fetchDeferredOrders();
  };

  const handleCancelDeferred = async (orderId) => {
    try { await axios.delete(`${API}/deferred-orders/${orderId}`); toast.success('Parked order cancelled'); fetchDeferredOrders(); }
    catch (err) { toast.error('Failed to cancel'); }
  };

  const loadPendingSales = async () => { const sales = await getPendingSales(); setPendingSales(sales); setShowOfflineQueue(true); };

  return (
    <div className="till-layout" data-testid="till-page">
      <CategorySidebar categories={categories} activeCategory={activeCategory} displayGroup={displayGroup} onSelect={handleCategorySelect} />

      <div className="flex flex-col bg-slate-50 overflow-hidden min-h-0">
        {/* Header bar */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200">
          <h1 className="font-heading text-2xl font-bold text-slate-900">Kasseport</h1>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => setShowDiscount(true)} data-testid="discount-btn">
              <Tag className="w-4 h-4 mr-1" /> Discount
            </Button>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer ${isOnline ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}
              onClick={loadPendingSales} data-testid="offline-status">
              {isOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              <span>{isOnline ? 'Online' : 'Offline'}</span>
              {pendingCount > 0 && <span className="bg-amber-500 text-white px-2 py-0.5 rounded-full text-xs">{pendingCount}</span>}
            </div>
            {pendingCount > 0 && isOnline && (
              <Button variant="outline" size="sm" onClick={handleSync} disabled={isSyncing}>
                <RefreshCw className={`w-4 h-4 mr-1 ${isSyncing ? 'animate-spin' : ''}`} /> Sync
              </Button>
            )}
            <span className="text-sm text-slate-600 font-body">{user?.username}{user?.user_code ? ` (#${user.user_code})` : ''}</span>
            <Button variant="outline" size="sm" data-testid="park-order-btn" onClick={handleParkOrder} disabled={items.length === 0}
              className="text-amber-700 border-amber-300 hover:bg-amber-50">
              <PauseCircle className="w-4 h-4 mr-1" /> Park
            </Button>
            <Button variant="outline" size="sm" data-testid="recall-order-btn"
              onClick={() => { fetchDeferredOrders(); setShowDeferredDialog(true); }} className="relative">
              <PlayCircle className="w-4 h-4 mr-1" /> Recall
              {deferredOrders.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{deferredOrders.length}</span>
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowPinDialog(true)} data-testid="till-change-pin-btn" title="Change PIN">
              <KeyRound className="w-4 h-4" />
            </Button>
            {(user?.role === 'admin' || user?.role === 'chef') && (
              <Button variant="outline" size="sm" onClick={() => navigate('/admin')}>Admin</Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>

        {/* Customer Loyalty Bar */}
        <div className="flex items-center gap-2 px-4 py-2 bg-white border-b border-slate-100">
          {linkedCustomer ? (
            <div className="flex items-center gap-3 flex-1">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200">
                <Award className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">{linkedCustomer.first_name} {linkedCustomer.surname}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 font-bold">{linkedCustomer.points_balance} pts</span>
                <button onClick={() => { setLinkedCustomer(null); setRedemptionChoice(null); }} className="ml-1 text-amber-400 hover:text-amber-600"><X className="w-3 h-3" /></button>
              </div>
              {loyaltySettings?.enabled && linkedCustomer.points_balance >= (loyaltySettings?.redemption_rate || 100) && items.length > 0 && (
                <Button variant="outline" size="sm" data-testid="redeem-points-btn" onClick={() => setShowRedemption(true)}>
                  <Award className="w-3 h-3 mr-1" /> Redeem
                </Button>
              )}
              {redemptionChoice && (
                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                  Redeeming: {redemptionChoice.type === 'meal_deal' ? 'Meal Deal' : `${redemptionChoice.points} pts`}
                  <button onClick={() => setRedemptionChoice(null)} className="ml-1 text-green-500 hover:text-green-700"><X className="w-3 h-3 inline" /></button>
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <Button variant="ghost" size="sm" data-testid="link-customer-btn" onClick={() => setShowCustomerSearch(true)} className="text-slate-500 hover:text-slate-700">
                <Phone className="w-4 h-4 mr-1" /> Link Customer
              </Button>
            </div>
          )}
        </div>

        <ProductGrid products={products} categories={categories} activeCategory={activeCategory} displayGroup={displayGroup}
          onAddToBasket={handleAddToBasket} expandedProduct={expandedProduct} onExpand={setExpandedProduct} allProducts={products}
          favourites={favourites} />
      </div>

      <Basket onCompleteSale={handleCompleteSale} loading={loading} vatRate={vatRate} discount={discount} setDiscount={setDiscount} />

      {/* Modals */}
      <PaymentModal isOpen={showPayment} onClose={() => setShowPayment(false)}
        subtotal={paymentData?.subtotal || 0} vatAmount={paymentData?.vatAmount || 0} total={paymentData?.total || 0}
        discount={paymentData?.discount || 0} onConfirm={handlePaymentConfirm}
        settings={settings?.payment_methods} canUseAccount={linkedCustomer?.can_use_account_payment || user?.role === 'admin'} />
      <DiscountModal isOpen={showDiscount} onClose={() => setShowDiscount(false)} onApply={setDiscount} />
      <SaleConfirmation sale={completedSale} onClose={() => setCompletedSale(null)} onPrint={handlePrintReceipt} />
      <OfflineQueueDialog isOpen={showOfflineQueue} onClose={() => setShowOfflineQueue(false)}
        isOnline={isOnline} pendingSales={pendingSales} isSyncing={isSyncing} onSync={handleSync} />
      <CustomerSearchDialog isOpen={showCustomerSearch} onClose={() => setShowCustomerSearch(false)}
        customerPhone={customerPhone} setCustomerPhone={setCustomerPhone} onSearch={searchCustomer} />
      <NewCustomerDialog isOpen={showNewCustomer} onClose={() => setShowNewCustomer(false)}
        form={newCustForm} setForm={setNewCustForm} onSubmit={createAndLinkCustomer} />
      <RedemptionDialog isOpen={showRedemption} onClose={() => setShowRedemption(false)}
        linkedCustomer={linkedCustomer} loyaltySettings={loyaltySettings} onRedeem={setRedemptionChoice} />
      <DeferredOrdersDialog isOpen={showDeferredDialog} onClose={() => setShowDeferredDialog(false)}
        orders={deferredOrders} onRecall={handleRecallOrder} onCancel={handleCancelDeferred} />
      <ChangePinDialog open={showPinDialog} onOpenChange={setShowPinDialog} />
    </div>
  );
};

const TillPage = () => (
  <BasketProvider>
    <TillContent />
  </BasketProvider>
);

export default TillPage;
